import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/** Identifiant d'un bloc JSON-LD : sert de clé pour le remplacer ou le retirer. */
export type JsonLdSlot = string;

/**
 * Écrit des blocs `<script type="application/ld+json">` dans le `<head>`.
 *
 * Passe par le `DOCUMENT` injecté et non par le global `document` : côté serveur,
 * Angular fournit une implémentation DOM, ce qui permet au balisage d'être présent
 * dans le HTML rendu par le SSR — le seul endroit où les crawlers le liront. Les
 * balises `<script>` placées dans un template Angular sont, elles, retirées par la
 * sanitisation : cette approche est la seule qui fonctionne.
 */
@Injectable({ providedIn: 'root' })
export class JsonLdService {

	private static readonly ATTRIBUTE = 'data-json-ld';

	private readonly document = inject(DOCUMENT);

	/**
	 * Insère ou remplace le bloc JSON-LD identifié par `slot`.
	 * `data` à `null` ou tableau vide retire le bloc.
	 */
	set(slot: JsonLdSlot, data: unknown | null): void {
		this.remove(slot);

		if (data === null || data === undefined) return;
		if (Array.isArray(data) && data.length === 0) return;

		const head = this.document.head;
		if (!head) return;

		const script = this.document.createElement('script');
		script.setAttribute('type', 'application/ld+json');
		script.setAttribute(JsonLdService.ATTRIBUTE, slot);
		script.textContent = JsonLdService.serialize(data);
		head.appendChild(script);
	}

	/** Retire le bloc identifié par `slot`, s'il existe. */
	remove(slot: JsonLdSlot): void {
		const head = this.document.head;
		if (!head) return;

		for (const node of Array.from(head.children)) {
			if (node.getAttribute(JsonLdService.ATTRIBUTE) === slot) {
				node.remove();
			}
		}
	}

	/**
	 * `</script>` dans une valeur refermerait la balise et casserait la page :
	 * on échappe `<`, ce qui reste du JSON valide et neutralise le cas.
	 */
	private static serialize(data: unknown): string {
		return JSON.stringify(data).replace(/</g, '\\u003c');
	}
}
